import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import toast from 'react-hot-toast';
import { MapPin, Star, Globe, Users, ChevronDown, ChevronRight, Languages, RefreshCw, AlertTriangle, CheckCircle2, Loader2, Trash2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useGuides } from '../contexts/GuidesContext';
import { sanitizeFilename, generateSafeGuideFilename } from './ItineraryCreator/utils/filenameSanitizer';
import { fetchGuideWithAuth } from '../utils/authenticatedFetchGuide';
import { getFirebaseAuthToken } from '../utils/firebaseAuthToken';
import { compareGuideSharedContent, compareGuideTranslationContent } from './features/linkedLanguageUtils';
import { buildTranslationStateUrl } from './ItineraryCreator/utils/storageUtils';
import { groupPublishedGuides, isOriginalGuide, matchesPublishedGuideSearch } from './utils/publishedGuides';

const ITINERARY_STORAGE_PROXY_URL = 'https://us-central1-guidesofia-access-control.cloudfunctions.net/backendSaveItineraryJsonToFirebase';
const UNPUBLISH_GUIDE_ENDPOINT = 'https://us-central1-guidesofia-access-control.cloudfunctions.net/unpublishGuide';

const deriveStoragePathFromUrl = (url) => {
  if (!url) return '';

  try {
    const parsed = new URL(url);

    if (parsed.hostname === 'storage.googleapis.com') {
      const parts = parsed.pathname.split('/').filter(Boolean);
      if (parts.length >= 2) {
        return decodeURIComponent(parts.slice(1).join('/'));
      }
    }

    if (parsed.hostname.includes('firebasestorage.googleapis.com')) {
      const match = parsed.pathname.match(/\/o\/(.+)$/);
      if (match?.[1]) {
        return decodeURIComponent(match[1].split('?')[0]);
      }
    }
  } catch (_error) {
    return '';
  }

  return '';
};

const buildGuideStoragePreview = (guide) => {
  const jsonPath = deriveStoragePathFromUrl(guide?.Website || '');
  if (!jsonPath) {
    return null;
  }

  const pathParts = jsonPath.split('/').filter(Boolean);
  const fileName = pathParts[pathParts.length - 1] || '';
  const folderParts = pathParts.slice(0, -1);
  const storagePrefix = folderParts.join('/');
  const folderName = folderParts[folderParts.length - 1] || fileName.replace(/\.json$/i, '');
  const translationStatePath = fileName.endsWith('.json')
    ? jsonPath.replace(/\.json$/i, '.translation-state.json')
    : '';
  const routesPath = storagePrefix && folderName
    ? `${storagePrefix}/${folderName}_routes.json`
    : '';

  return {
    jsonPath,
    storagePrefix,
    translationStatePath,
    routesPath
  };
};

const buildGuideEditorUrl = (guide) => {
  if (!guide?.Website) return '#';

  const nextUrl = new URL(window.location.href);
  nextUrl.searchParams.set('section', 'builder');
  nextUrl.searchParams.set('tab', 'metadata');
  nextUrl.searchParams.set('loadGuideUrl', guide.Website);
  nextUrl.searchParams.set('loadGuideId', String(guide.ID || ''));
  return nextUrl.toString();
};

const GuidesListing = ({ onLoadGuide }) => {
  const { currentUser, userRole } = useAuth();
  const { fetchGuides, getCachedGuides, lastFetchTime, loading, error } = useGuides();
  const [guides, setGuides] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState('all');
  const [loadingGuide, setLoadingGuide] = useState(null);
  const [guidesWithJsonIds, setGuidesWithJsonIds] = useState({});
  const [guideLogoUrls, setGuideLogoUrls] = useState({});
  const [expandedGroups, setExpandedGroups] = useState(new Set());
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [groupSyncStatuses, setGroupSyncStatuses] = useState({});
  const [loadingGroupStatuses, setLoadingGroupStatuses] = useState({});
  const [groupSyncErrors, setGroupSyncErrors] = useState({});
  const [pendingUnpublishGuideId, setPendingUnpublishGuideId] = useState(null);
  const [unpublishPreview, setUnpublishPreview] = useState(null);
  const [unpublishingGuideId, setUnpublishingGuideId] = useState(null);
  const guideDataCacheRef = useRef({});
  const guideTranslationStateCacheRef = useRef({});

  // API URL - Always use Firebase Function
  const API_BASE_URL = 'https://us-central1-guidesofia-access-control.cloudfunctions.net';

  useEffect(() => {
    const loadGuides = async () => {
      const isAndroid = /Android/i.test(navigator.userAgent);

      // Check if we have cached data for the current country
      let cachedGuides = getCachedGuides(selectedCountry);

      // Android fallback: check sessionStorage directly if context cache is empty
      if (!cachedGuides && isAndroid) {
        try {
          const storedCache = sessionStorage.getItem('guidesCache');
          if (storedCache) {
            const cache = JSON.parse(storedCache);
            const cacheKey = selectedCountry || 'all';
            cachedGuides = cache[cacheKey];
          }
        } catch (error) {
          console.warn('Android: sessionStorage fallback failed:', error);
        }
      }

      if (cachedGuides && !isRefreshing) {
        // Use cached data
        setGuides(cachedGuides);
        return;
      }

      if (currentUser && !loading) {
        try {
          if (!isRefreshing) setIsRefreshing(true);
          const result = await fetchGuides(selectedCountry, isRefreshing);
          setGuides(result.guides);
        } catch (err) {
          console.error('Failed to load guides:', err);
        } finally {
          setIsRefreshing(false);
        }
      }
    };

    loadGuides();
  }, [selectedCountry, currentUser, isRefreshing, fetchGuides, getCachedGuides]);

  // Initial load when component first mounts - especially important for Android
  useEffect(() => {
    const isAndroid = /Android/i.test(navigator.userAgent);

    const cachedGuides = getCachedGuides(selectedCountry);
    if (cachedGuides) {
      setGuides(cachedGuides);
    } else {
      // On Android, also check sessionStorage directly as fallback
      if (isAndroid) {
        try {
          const storedCache = sessionStorage.getItem('guidesCache');
          if (storedCache) {
            const cache = JSON.parse(storedCache);
            const cacheKey = selectedCountry || 'all';
            if (cache[cacheKey]) {
              setGuides(cache[cacheKey]);
            }
          }
        } catch (error) {
          console.warn('Android: Failed to recover from sessionStorage:', error);
        }
      }
    }
  }, [getCachedGuides, selectedCountry]);

  const groupedGuides = useMemo(() => groupPublishedGuides(guides), [guides]);
  const filteredGroupedGuides = useMemo(
    () => groupedGuides.filter((group) => matchesPublishedGuideSearch(group, searchQuery)),
    [groupedGuides, searchQuery]
  );

  // Toggle group expansion
  const toggleGroup = (groupId) => {
    const newExpanded = new Set(expandedGroups);
    if (newExpanded.has(groupId)) {
      newExpanded.delete(groupId);
    } else {
      newExpanded.add(groupId);
    }
    setExpandedGroups(newExpanded);
  };

  const getGuideData = useCallback(async (guide, forceRefresh = false) => {
    if (!guide?.Website) {
      throw new Error(`Guide ${guide?.Name || guide?.ID || 'unknown'} is missing a JSON URL.`);
    }

    const cachedGuideData = guideDataCacheRef.current[guide.ID];
    if (!forceRefresh && cachedGuideData) {
      return cachedGuideData;
    }

    const response = await fetchGuideWithAuth(currentUser, guide.Website);
    if (!response.ok) {
      throw new Error(`Failed to fetch ${guide.Name || guide.ID}: ${response.status}`);
    }

    const guideData = await response.json();
    guideDataCacheRef.current[guide.ID] = guideData;
    return guideData;
  }, [currentUser]);

  const getGuideTranslationState = useCallback(async (guide, forceRefresh = false) => {
    if (!guide?.Website) {
      return null;
    }

    if (!forceRefresh && Object.prototype.hasOwnProperty.call(guideTranslationStateCacheRef.current, guide.ID)) {
      return guideTranslationStateCacheRef.current[guide.ID];
    }

    const translationStateUrl = buildTranslationStateUrl(guide.Website);
    if (!translationStateUrl) {
      guideTranslationStateCacheRef.current[guide.ID] = null;
      return null;
    }

    try {
      const response = await fetchGuideWithAuth(currentUser, translationStateUrl);
      if (response.status === 404) {
        guideTranslationStateCacheRef.current[guide.ID] = null;
        return null;
      }
      if (!response.ok) {
        throw new Error(`Failed to fetch translation state for ${guide.Name || guide.ID}: ${response.status}`);
      }

      const translationState = await response.json();
      guideTranslationStateCacheRef.current[guide.ID] = translationState;
      return translationState;
    } catch (error) {
      if (String(error.message || '').includes('404')) {
        guideTranslationStateCacheRef.current[guide.ID] = null;
        return null;
      }
      throw error;
    }
  }, [currentUser]);

  const loadGroupSyncStatus = useCallback(async (group, forceRefresh = false) => {
    if (!currentUser || !group?.id || group.guides.length <= 1) {
      return;
    }

    if (!forceRefresh && (groupSyncStatuses[group.id] || loadingGroupStatuses[group.id])) {
      return;
    }

    setLoadingGroupStatuses((prev) => ({
      ...prev,
      [group.id]: true
    }));
    setGroupSyncErrors((prev) => ({
      ...prev,
      [group.id]: ''
    }));

    try {
      if (forceRefresh) {
        const nextCache = { ...guideDataCacheRef.current };
        group.guides.forEach((guide) => {
          delete nextCache[guide.ID];
        });
        guideDataCacheRef.current = nextCache;
      }

      const primaryGuide = group.primaryGuide || group.guides[0];
      const primaryGuideData = await getGuideData(primaryGuide);
      const relatedStatuses = {};
      const translationStatuses = {};
      let outOfSyncCount = 0;
      let translationReviewCount = 0;
      let translationBaselineCount = 0;

      for (const guide of group.guides) {
        const isPrimary = String(guide.ID) === String(primaryGuide.ID);
        if (isPrimary) {
          relatedStatuses[guide.ID] = {
            guideId: guide.ID,
            isPrimary: true,
            totalDiffs: 0,
            metadataDiffs: [],
            pointDiffs: []
          };
          translationStatuses[guide.ID] = {
            guideId: guide.ID,
            isPrimary: true,
            needsReview: false,
            totalIssues: 0,
            reviewBaselineMissing: false,
            sourceChangedSinceReview: false
          };
          continue;
        }

          const guideData = await getGuideData(guide, forceRefresh);
          const translationState = await getGuideTranslationState(guide, forceRefresh);
          const comparison = compareGuideSharedContent(primaryGuideData, guideData);
          const translationComparison = compareGuideTranslationContent(primaryGuideData, guideData, translationState);
        relatedStatuses[guide.ID] = {
          guideId: guide.ID,
          isPrimary: false,
          ...comparison
        };
        translationStatuses[guide.ID] = {
          guideId: guide.ID,
          isPrimary: false,
          ...translationComparison
        };

        if (comparison.totalDiffs > 0) {
          outOfSyncCount += 1;
        }
        if (translationComparison.needsReview) {
          translationReviewCount += 1;
        }
        if (translationComparison.reviewBaselineMissing) {
          translationBaselineCount += 1;
        }
      }

      setGroupSyncStatuses((prev) => ({
        ...prev,
        [group.id]: {
          primaryGuideId: primaryGuide.ID,
          primaryGuideName: primaryGuide.Name,
          guideStatuses: relatedStatuses,
          translationStatuses,
          outOfSyncCount,
          translationReviewCount,
          translationBaselineCount,
          comparedGuides: Math.max(group.guides.length - 1, 0),
          checkedAt: new Date().toISOString()
        }
      }));
    } catch (statusError) {
      console.error(`Failed to load sync status for group ${group.id}:`, statusError);
      setGroupSyncErrors((prev) => ({
        ...prev,
        [group.id]: statusError.message || 'Failed to compare linked guides'
      }));
    } finally {
      setLoadingGroupStatuses((prev) => ({
        ...prev,
        [group.id]: false
      }));
    }
  }, [currentUser, getGuideData, getGuideTranslationState, groupSyncStatuses, loadingGroupStatuses]);

  // Check JSON IDs for all guides to enable better grouping
  const checkAllJsonIds = async () => {
    const guidesToCheck = guides.filter(guide =>
      guide.Website && !guidesWithJsonIds[guide.ID]
    );

    for (const guide of guidesToCheck) {
      await checkJsonId(guide);
    }
  };

  // Check JSON ID for a guide
  const checkJsonId = async (guide) => {
    if (!guide.Website || guidesWithJsonIds[guide.ID]) {
      return; // Already checked or no URL
    }

    try {
      const response = await fetchGuideWithAuth(currentUser, guide.Website);

      if (response.ok) {
        const guideData = await response.json();
        const jsonId = guideData.metadata?.id;

        setGuidesWithJsonIds(prev => ({
          ...prev,
          [guide.ID]: {
            jsonId,
            needsFix: jsonId !== guide.ID,
            parentGuideId: guideData.metadata?.parent_guide_id
          }
        }));
      }
    } catch (error) {
      // Silently fail - not critical
    }
  };

  const loadGuideLogo = useCallback(async (guide) => {
    if (!guide?.Website || guideLogoUrls[guide.ID] !== undefined) {
      return;
    }

    try {
      const response = await fetchGuideWithAuth(currentUser, guide.Website);
      if (!response.ok) {
        throw new Error(`Failed to fetch guide JSON: ${response.status}`);
      }

      const guideData = await response.json();
      const logoUrl = guideData?.metadata?.museum_info?.logoUrl || '';

      setGuideLogoUrls((prev) => ({
        ...prev,
        [guide.ID]: logoUrl
      }));
    } catch (error) {
      console.warn(`Failed to load business logo for guide ${guide.ID}:`, error);
      setGuideLogoUrls((prev) => ({
        ...prev,
        [guide.ID]: ''
      }));
    }
  }, [currentUser, guideLogoUrls]);

  useEffect(() => {
    if (!currentUser) return;

    groupedGuides.forEach((group) => {
      if (!expandedGroups.has(group.id)) return;
      group.guides.forEach((guide) => {
        if (guideLogoUrls[guide.ID] === undefined) {
          loadGuideLogo(guide);
        }
      });
    });
  }, [groupedGuides, expandedGroups, currentUser, guideLogoUrls, loadGuideLogo]);

  useEffect(() => {
    if (!currentUser) return;

    groupedGuides.forEach((group) => {
      if (!expandedGroups.has(group.id)) return;
      if (group.guides.length <= 1) return;
      if (groupSyncStatuses[group.id] || loadingGroupStatuses[group.id]) return;
      loadGroupSyncStatus(group);
    });
  }, [groupedGuides, expandedGroups, currentUser, groupSyncStatuses, loadingGroupStatuses, loadGroupSyncStatus]);

  const handleRefresh = async () => {
    try {
      setIsRefreshing(true);
      const result = await fetchGuides(selectedCountry, true);
      setGuides(result.guides);
    } catch (err) {
      console.error('Failed to refresh guides:', err);
    } finally {
      setIsRefreshing(false);
    }
  };

  const getCountries = () => {
    const countries = [...new Set(guides.map(guide => guide.Country))];
    return countries.filter(country => country).sort();
  };

  const formatTags = (tags) => {
    if (!tags) return [];
    try {
      return Array.isArray(tags) ? tags : JSON.parse(tags);
    } catch {
      return [];
    }
  };

  const getGroupSyncSummary = (group) => {
    if (group.guides.length <= 1) {
      return {
        label: 'Single version',
        className: 'bg-gray-100 text-gray-700',
        icon: null
      };
    }

    if (loadingGroupStatuses[group.id]) {
      return {
        label: 'Checking sync...',
        className: 'bg-sky-100 text-sky-700',
        icon: <Loader2 className="w-3.5 h-3.5 animate-spin" />
      };
    }

    if (groupSyncErrors[group.id]) {
      return {
        label: 'Sync status unavailable',
        className: 'bg-rose-100 text-rose-700',
        icon: <AlertTriangle className="w-3.5 h-3.5" />
      };
    }

    const summary = groupSyncStatuses[group.id];
    if (!summary) {
      return {
        label: 'Open to check sync',
        className: 'bg-amber-100 text-amber-800',
        icon: <AlertTriangle className="w-3.5 h-3.5" />
      };
    }

    if (summary.outOfSyncCount === 0) {
      return {
        label: 'Shared fields in sync',
        className: 'bg-emerald-100 text-emerald-700',
        icon: <CheckCircle2 className="w-3.5 h-3.5" />
      };
    }

    return {
      label: `${summary.outOfSyncCount} ${summary.outOfSyncCount === 1 ? 'version needs sync' : 'versions need sync'}`,
      className: 'bg-amber-100 text-amber-800',
      icon: <AlertTriangle className="w-3.5 h-3.5" />
    };
  };

  const getGuideSyncBadge = (groupId, guideId) => {
    const status = groupSyncStatuses[groupId]?.guideStatuses?.[guideId];

    if (loadingGroupStatuses[groupId]) {
      return {
        label: 'Checking shared sync...',
        className: 'bg-sky-100 text-sky-700'
      };
    }

    if (groupSyncErrors[groupId]) {
      return {
        label: 'Shared sync unavailable',
        className: 'bg-rose-100 text-rose-700'
      };
    }

    if (!status) {
      return null;
    }

    if (status.isPrimary) {
      return {
        label: 'Master shared source',
        className: 'bg-emerald-100 text-emerald-700'
      };
    }

    if (status.totalDiffs === 0) {
      return {
        label: 'Shared fields in sync',
        className: 'bg-emerald-100 text-emerald-700'
      };
    }

    return {
      label: `${status.totalDiffs} shared diff${status.totalDiffs === 1 ? '' : 's'}`,
      className: 'bg-amber-100 text-amber-800'
    };
  };

  const getGroupTranslationSummary = (group) => {
    if (group.guides.length <= 1) {
      return {
        label: 'No translation review needed',
        className: 'bg-gray-100 text-gray-700',
        icon: null
      };
    }

    if (loadingGroupStatuses[group.id]) {
      return {
        label: 'Checking text review...',
        className: 'bg-sky-100 text-sky-700',
        icon: <Loader2 className="w-3.5 h-3.5 animate-spin" />
      };
    }

    if (groupSyncErrors[group.id]) {
      return {
        label: 'Text review unavailable',
        className: 'bg-rose-100 text-rose-700',
        icon: <AlertTriangle className="w-3.5 h-3.5" />
      };
    }

    const summary = groupSyncStatuses[group.id];
    if (!summary) {
      return {
        label: 'Open to check text review',
        className: 'bg-violet-100 text-violet-800',
        icon: <AlertTriangle className="w-3.5 h-3.5" />
      };
    }

    if (summary.translationBaselineCount > 0) {
      return {
        label: `${summary.translationBaselineCount} ${summary.translationBaselineCount === 1 ? 'version needs baseline' : 'versions need baseline'}`,
        className: 'bg-violet-100 text-violet-800',
        icon: <AlertTriangle className="w-3.5 h-3.5" />
      };
    }

    if (summary.translationReviewCount === 0) {
      return {
        label: 'Text review current',
        className: 'bg-emerald-100 text-emerald-700',
        icon: <CheckCircle2 className="w-3.5 h-3.5" />
      };
    }

    return {
      label: `${summary.translationReviewCount} ${summary.translationReviewCount === 1 ? 'version needs text review' : 'versions need text review'}`,
      className: 'bg-violet-100 text-violet-800',
      icon: <AlertTriangle className="w-3.5 h-3.5" />
    };
  };

  const getGuideTranslationBadge = (groupId, guideId) => {
    const status = groupSyncStatuses[groupId]?.translationStatuses?.[guideId];

    if (loadingGroupStatuses[groupId]) {
      return {
        label: 'Checking text review...',
        className: 'bg-sky-100 text-sky-700'
      };
    }

    if (groupSyncErrors[groupId]) {
      return {
        label: 'Text review unavailable',
        className: 'bg-rose-100 text-rose-700'
      };
    }

    if (!status) {
      return null;
    }

    if (status.isPrimary) {
      return {
        label: 'Master text source',
        className: 'bg-emerald-100 text-emerald-700'
      };
    }

    if (status.reviewBaselineMissing) {
      return {
        label: 'Text review baseline missing',
        className: 'bg-violet-100 text-violet-800'
      };
    }

    if (!status.needsReview) {
      return {
        label: 'Text review current',
        className: 'bg-emerald-100 text-emerald-700'
      };
    }

    if (status.sourceChangedSinceReview) {
      return {
        label: 'Source text changed',
        className: 'bg-violet-100 text-violet-800'
      };
    }

    return {
      label: `${status.totalIssues} text issue${status.totalIssues === 1 ? '' : 's'}`,
      className: 'bg-violet-100 text-violet-800'
    };
  };

  const handleLoadGuide = async (guide) => {
    if (!guide.Website) {
      toast.error('No guide URL available');
      return;
    }

    setLoadingGuide(guide.ID);
    const loadingToast = toast.loading(`Loading "${guide.Name}"...`);

    try {
      const response = await fetchGuideWithAuth(currentUser, guide.Website);

      if (!response.ok) {
        throw new Error(`Failed to fetch guide: ${response.status}`);
      }

      const guideData = await response.json();

      // Call the parent component's load function if provided
      if (onLoadGuide) {
        onLoadGuide(guideData, guide.ID, guide.Website); // Pass database ID and exact source URL
        toast.success(`Guide "${guide.Name}" loaded successfully!`, { id: loadingToast });
      } else {
        // Fallback: toast user
        toast.success(`Guide "${guide.Name}" loaded successfully!`, { id: loadingToast });
      }

    } catch (error) {
      console.error('Error loading guide:', error);
      toast.error(`Failed to load guide: ${error.message}`, { id: loadingToast });
    } finally {
      setLoadingGuide(null);
    }
  };

  const handleFixGuideId = async (guide) => {
    if (!guide.Website) {
      toast.error('No guide URL available');
      return;
    }

    if (!window.confirm(`This will update the guide's JSON file ID from its current value to ${guide.ID} (database ID). Continue?`)) {
      return;
    }

    setLoadingGuide(guide.ID);

    try {
      const response = await fetchGuideWithAuth(currentUser, guide.Website);

      if (!response.ok) {
        throw new Error(`Failed to fetch guide: ${response.status}`);
      }

      const guideData = await response.json();
      const originalId = guideData.metadata?.id;

      // Update the metadata ID to match database ID
      const updatedGuideData = {
        ...guideData,
        metadata: {
          ...guideData.metadata,
          id: guide.ID,
          updatedAt: new Date().toISOString(),
          idFixedAt: new Date().toISOString(),
          originalId: originalId // Keep track of the original ID
        }
      };

      // Save the updated guide back to Firebase Storage
      try {
        const token = await getFirebaseAuthToken(currentUser);
        const saveResponse = await fetch(ITINERARY_STORAGE_PROXY_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({
            itineraryData: updatedGuideData,
            storageConfig: {
              country: sanitizeFilename(updatedGuideData.metadata.country || 'unknown', { maxLength: 20, preserveExtension: false }),
              itineraryName: updatedGuideData.metadata.filename?.replace(/\.json$/, '') ||
                           generateSafeGuideFilename(updatedGuideData.metadata).replace(/\.json$/, ''),
              filename: updatedGuideData.metadata.filename ?
                       sanitizeFilename(updatedGuideData.metadata.filename, { maxLength: 100 }) :
                       generateSafeGuideFilename(updatedGuideData.metadata),
              forceUpdate: true
            }
          })
        });

        const saveResult = await saveResponse.json();

        if (saveResult.success) {
          alert(`Guide ID fixed successfully!\nUpdated from "${originalId || 'undefined'}" to "${guide.ID}"\nFile saved to Firebase Storage.`);

          // Update the local state to reflect the fix
          setGuidesWithJsonIds(prev => ({
            ...prev,
            [guide.ID]: {
              jsonId: guide.ID,
              needsFix: false
            }
          }));
        } else {
          throw new Error(saveResult.error || 'Failed to save updated guide');
        }
      } catch (saveError) {
        console.error('Error saving updated guide:', saveError);
        throw new Error(`Failed to save updated guide: ${saveError.message}`);
      }

    } catch (error) {
      console.error('Error fixing guide ID:', error);
      alert(`Failed to fix guide ID: ${error.message}`);
    } finally {
      setLoadingGuide(null);
    }
  };

  const handlePrepareUnpublish = (guide) => {
    const preview = buildGuideStoragePreview(guide);
    setPendingUnpublishGuideId(guide.ID);
    setUnpublishPreview(preview);
  };

  const handleCancelUnpublish = () => {
    setPendingUnpublishGuideId(null);
    setUnpublishPreview(null);
  };

  const handleConfirmUnpublish = async (guide) => {
    try {
      const preview = buildGuideStoragePreview(guide);
      if (!preview?.storagePrefix) {
        throw new Error('Unable to derive the Firebase Storage path for this guide.');
      }

      setUnpublishingGuideId(guide.ID);
      const token = await getFirebaseAuthToken(currentUser);
      const response = await fetch(UNPUBLISH_GUIDE_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          guideId: guide.ID,
          fileUrl: guide.Website,
          storagePrefix: preview.storagePrefix
        })
      });

      const result = await response.json().catch(() => ({}));
      if (!response.ok || !result.success) {
        throw new Error(result.error || `Failed to unpublish guide (${response.status})`);
      }

      setGuides((prev) => prev.filter((item) => String(item.ID) !== String(guide.ID)));
      delete guideDataCacheRef.current[guide.ID];
      delete guideTranslationStateCacheRef.current[guide.ID];
      setPendingUnpublishGuideId(null);
      setUnpublishPreview(null);
      toast.success(`Unpublished "${guide.Name}"`);
    } catch (deleteError) {
      console.error('Failed to unpublish guide:', deleteError);
      toast.error(deleteError.message || 'Failed to unpublish guide');
    } finally {
      setUnpublishingGuideId(null);
    }
  };

  const canUnpublishGuides = userRole === 'admin' || userRole === 'superadmin';

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-sm p-6">
        <h3 className="text-xl font-semibold mb-4">Published Guides</h3>
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          <span className="ml-2 text-gray-600">Loading guides...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-xl shadow-sm p-6">
        <h3 className="text-xl font-semibold mb-4">Published Guides</h3>
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-600">{error}</p>
          <button
            onClick={handleRefresh}
            className="mt-2 px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-sm p-6">
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-4">
          <h3 className="text-xl font-semibold">Published Guides ({guides.length})</h3>
          {lastFetchTime && (
            <span className="text-sm text-gray-500">
              Last updated: {lastFetchTime.toLocaleTimeString()}
            </span>
          )}
        </div>

        <div className="flex items-center gap-3">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-64 px-3 py-2 border border-gray-300 rounded-lg text-sm"
            placeholder="Search guides, city, language, ID"
          />

          {/* Reload Button */}
          <button
            onClick={handleRefresh}
            disabled={loading || isRefreshing}
            className="px-3 py-2 bg-blue-500 text-white rounded-lg text-sm hover:bg-blue-600 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2"
            title="Refresh guides from database"
          >
            <RefreshCw className={`w-4 h-4 ${(loading || isRefreshing) ? 'animate-spin' : ''}`} />
            {loading || isRefreshing ? 'Refreshing...' : 'Reload'}
          </button>

          {/* Country Filter */}
          <select
            value={selectedCountry}
            onChange={(e) => setSelectedCountry(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
          >
            <option value="all">All Countries</option>
            {getCountries().map(country => (
              <option key={country} value={country}>
                {country.toUpperCase()}
              </option>
            ))}
          </select>
        </div>
      </div>

      {guides.length === 0 ? (
        <p className="text-gray-500 text-center py-8">No guides found.</p>
      ) : (
        <div className="space-y-6">
          {filteredGroupedGuides.length === 0 && (
            <div className="rounded-lg border border-dashed border-gray-300 p-6 text-center text-sm text-gray-500">
              No published guides match this search.
            </div>
          )}
          {filteredGroupedGuides.map((group) => {
            const groupSyncSummary = getGroupSyncSummary(group);
            const groupTranslationSummary = getGroupTranslationSummary(group);

            return (
            <div key={group.id} className="border border-gray-300 rounded-lg overflow-hidden bg-white shadow-sm">
              {/* Group Header */}
              <div
                className="bg-gray-50 px-4 py-3 border-b border-gray-200 cursor-pointer hover:bg-gray-100 transition-colors"
                onClick={() => toggleGroup(group.id)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <button className="flex items-center gap-2 text-gray-700 hover:text-gray-900">
                      {expandedGroups.has(group.id) ? (
                        <ChevronDown className="w-5 h-5" />
                      ) : (
                        <ChevronRight className="w-5 h-5" />
                      )}
                    </button>
                    <div>
                      <h3 className="font-semibold text-lg text-gray-800">
                        {group.primaryGuide?.Translated_Name || group.primaryGuide?.Name || group.guides[0]?.Name}
                      </h3>
                      <div className="flex items-center gap-4 text-sm text-gray-600">
                        <div className="flex items-center gap-1">
                          <MapPin className="w-4 h-4" />
                          <span>{group.primaryGuide?.Locality || group.guides[0]?.Locality}, {(group.primaryGuide?.Country || group.guides[0]?.Country)?.toUpperCase()}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Languages className="w-4 h-4" />
                          <span>{group.guides.length} {group.guides.length === 1 ? 'version' : 'versions'}</span>
                        </div>
                        <div className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium ${groupSyncSummary.className}`}>
                          {groupSyncSummary.icon}
                          <span>{groupSyncSummary.label}</span>
                        </div>
                        <div className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium ${groupTranslationSummary.className}`}>
                          {groupTranslationSummary.icon}
                          <span>{groupTranslationSummary.label}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    {group.guides.map(guide => {
                      const isOriginal = isOriginalGuide(guide);
                      return (
                        <span
                          key={guide.ID}
                          className={`px-2 py-1 text-xs rounded-full font-medium ${
                            isOriginal
                              ? 'bg-green-100 text-green-800 border border-green-400'
                              : 'bg-blue-100 text-blue-800'
                          }`}
                          title={`${guide.Language}${isOriginal ? ' (Original)' : ' (Translated)'} - ID: ${guide.ID}`}
                        >
                          {guide.Language}{isOriginal ? ' ★' : ''}
                        </span>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Group Content - Expanded */}
              {expandedGroups.has(group.id) && (
                <div className="p-4">
                  {groupSyncErrors[group.id] && (
                    <div className="mb-4 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
                      {groupSyncErrors[group.id]}
                    </div>
                  )}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {group.guides.map((guide) => {
                      const guideSyncBadge = getGuideSyncBadge(group.id, guide.ID);
                      const guideTranslationBadge = getGuideTranslationBadge(group.id, guide.ID);

                      return (
            <div key={guide.ID} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
              {guideSyncBadge && (
                <div className="mb-3">
                  <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium ${guideSyncBadge.className}`}>
                    {guideSyncBadge.label}
                  </span>
                </div>
              )}
              {guideTranslationBadge && (
                <div className="mb-3">
                  <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium ${guideTranslationBadge.className}`}>
                    {guideTranslationBadge.label}
                  </span>
                </div>
              )}
              {guideLogoUrls[guide.ID] && (
                <div className="mb-3 rounded-lg border border-slate-200 bg-slate-50 p-3">
                  <div className="flex h-16 items-center justify-center">
                    <img
                      src={guideLogoUrls[guide.ID]}
                      alt={`${guide.Name} logo`}
                      className="max-h-full max-w-full object-contain"
                      onError={() => {
                        setGuideLogoUrls((prev) => ({
                          ...prev,
                          [guide.ID]: ''
                        }));
                      }}
                    />
                  </div>
                </div>
              )}
              {/* Guide Image */}
              {guide.Photo && (
                <img
                  src={guide.Photo}
                  alt={guide.Name}
                  className="w-full h-40 object-cover rounded-lg mb-3"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              )}

              {/* Guide Info */}
              <div>
                <h4 className="font-semibold text-lg mb-2 line-clamp-2">
                  {guide.Translated_Name || guide.Name}
                </h4>

                <div className="flex items-center gap-2 text-sm text-gray-600 mb-2">
                  <MapPin className="w-4 h-4" />
                  <span>{guide.Locality}, {guide.Country?.toUpperCase()}</span>
                </div>

                {guide.Rating && (
                  <div className="flex items-center gap-2 text-sm text-gray-600 mb-2">
                    <Star className="w-4 h-4 text-yellow-500" />
                    <span>{guide.Rating}</span>
                    {guide.user_ratings_total && (
                      <span className="text-gray-500">({guide.user_ratings_total} reviews)</span>
                    )}
                  </div>
                )}

                <div className="flex items-center gap-2 text-sm text-gray-600 mb-3">
                  <Globe className="w-4 h-4" />
                  <span>{guide.Language}</span>
                </div>

                {/* Description */}
                {guide.Description && (
                  <p className="text-sm text-gray-700 mb-3 line-clamp-2">
                    {guide.Description}
                  </p>
                )}

                {/* Tags */}
                {formatTags(guide.Tags).length > 0 && (
                  <div className="mb-3">
                    <div className="flex flex-wrap gap-1">
                      {formatTags(guide.Tags).slice(0, 3).map((tag, index) => (
                        <span
                          key={index}
                          className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full"
                        >
                          {tag}
                        </span>
                      ))}
                      {formatTags(guide.Tags).length > 3 && (
                        <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">
                          +{formatTags(guide.Tags).length - 3} more
                        </span>
                      )}
                    </div>
                  </div>
                )}

                {/* Actions */}
                <div className="flex gap-2 flex-wrap">
                  {guide.Website && (
                    <a
                      href={buildGuideEditorUrl(guide)}
                      onClick={(event) => {
                        if (loadingGuide === guide.ID) {
                          event.preventDefault();
                          return;
                        }

                        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
                          return;
                        }

                        event.preventDefault();
                        handleLoadGuide(guide);
                      }}
                      aria-disabled={loadingGuide === guide.ID}
                      title="Left click loads the guide in this editor. Middle click, Ctrl-click, or Cmd-click opens this builder in a new tab and loads the same guide there."
                      className={`px-3 py-1 text-white text-sm rounded flex items-center gap-1 ${
                        loadingGuide === guide.ID
                          ? 'bg-gray-400 cursor-not-allowed pointer-events-none'
                          : 'bg-blue-500 hover:bg-blue-600'
                      }`}
                    >
                      {loadingGuide === guide.ID ? (
                        <>
                          <div className="w-3 h-3 border border-white border-t-transparent rounded-full animate-spin"></div>
                          Loading...
                        </>
                      ) : (
                        'Load Guide'
                      )}
                    </a>
                  )}

                  {/* Fix ID button - show if IDs don't match */}
                  {guidesWithJsonIds[guide.ID]?.needsFix && (
                    <button
                      onClick={() => handleFixGuideId(guide)}
                      disabled={loadingGuide === guide.ID}
                      className="px-3 py-1 bg-red-500 text-white text-sm rounded hover:bg-red-600 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-1"
                      title={`JSON ID: ${guidesWithJsonIds[guide.ID].jsonId} → DB ID: ${guide.ID}`}
                    >
                      Fix ID
                    </button>
                  )}

                  <span className={`px-3 py-1 text-sm rounded ${
                    guidesWithJsonIds[guide.ID]?.needsFix
                      ? 'bg-red-100 text-red-700'
                      : 'bg-gray-100 text-gray-700'
                  }`}>
                    DB ID: {guide.ID}
                    {guidesWithJsonIds[guide.ID] && (
                      <span className="block text-xs">
                        JSON ID: {guidesWithJsonIds[guide.ID].jsonId || 'undefined'}
                      </span>
                    )}
                  </span>

                  {canUnpublishGuides && guide.Website && (
                    <button
                      onClick={() => handlePrepareUnpublish(guide)}
                      disabled={unpublishingGuideId === guide.ID}
                      className="px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-1"
                    >
                      {unpublishingGuideId === guide.ID ? (
                        <>
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          Unpublishing...
                        </>
                      ) : (
                        <>
                          <Trash2 className="w-3.5 h-3.5" />
                          Unpublish
                        </>
                      )}
                    </button>
                  )}
                </div>

                {pendingUnpublishGuideId === guide.ID && (
                  <div className="mt-4 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-900">
                    <p className="font-semibold">Unpublish this guide?</p>
                    <p className="mt-1">
                      This will delete only the published database rows. Firebase files will stay in storage.
                    </p>
                    <div className="mt-3 space-y-2 text-xs text-red-800">
                      <p className="break-all">
                        <strong>Firebase folder:</strong> {unpublishPreview?.storagePrefix || 'Unavailable'}
                      </p>
                      <p className="break-all">
                        <strong>Main JSON:</strong> {unpublishPreview?.jsonPath || guide.Website}
                      </p>
                      {unpublishPreview?.translationStatePath && (
                        <p className="break-all">
                          <strong>Translation state:</strong> {unpublishPreview.translationStatePath}
                        </p>
                      )}
                      {unpublishPreview?.routesPath && (
                        <p className="break-all">
                          <strong>Routes file:</strong> {unpublishPreview.routesPath}
                        </p>
                      )}
                      <p>
                        <strong>Firebase files:</strong> preserved, not deleted
                      </p>
                      <p>
                        <strong>Database rows:</strong> `itineraries_catalog`, `itinerary_stops`, and `qr_codes` for guide ID {guide.ID}
                      </p>
                    </div>
                    <div className="mt-4 flex gap-2">
                      <button
                        onClick={() => handleConfirmUnpublish(guide)}
                        disabled={unpublishingGuideId === guide.ID}
                        className="px-3 py-1.5 bg-red-600 text-white text-sm rounded hover:bg-red-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                      >
                        Confirm Unpublish
                      </button>
                      <button
                        onClick={handleCancelUnpublish}
                        disabled={unpublishingGuideId === guide.ID}
                        className="px-3 py-1.5 bg-white border border-red-300 text-red-800 text-sm rounded hover:bg-red-100 disabled:bg-gray-100 disabled:text-gray-500"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default GuidesListing;
